import { COMPANY, fmtMoney } from "./pricing";
import { devWarn } from "./log";

export const MOWER_LABELS = {
  zero_turn: "Zero-Turn Mower",
  riding: "Riding Tractor",
  stand_on: "Stand-On Mower",
  compact_tractor: "Compact Tractor",
  utv: "UTV / Side-by-Side",
  golf_cart: "Golf Cart",
  implement: "Implement / Attachment",
  other: "Other",
};

export const esc = (s) =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

export function buildRfpHtml({ pickup, stops, effectiveMiles, schedule, units, photos, aiNotes, salesAgent }) {
  const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const validStops = (stops || []).filter((s) => s && s.trim());

  const stopRows = validStops
    .map((s, idx) => `<li>Stop ${idx + 1}: ${esc(s)}</li>`)
    .join("");

  const unitRows = (units || [])
    .map(
      (u, idx) => `<tr>
      <td>${idx + 1}</td>
      <td>${esc(MOWER_LABELS[u.type] || u.type || "-")}</td>
      <td>${esc(u.brand || "-")}</td>
      <td>${esc(u.model || "-")}</td>
      <td style="text-align:center">${esc(u.qty || 1)}</td>
      <td style="text-align:center">${esc(u.length || "-")} × ${esc(u.width || "-")} × ${esc(u.height || "-")} in</td>
      <td style="text-align:center">${esc(u.weight || "-")} lbs</td>
    </tr>`
    )
    .join("");

  const photoImgs = (photos || [])
    .map((p) => `<img src="${p.url || p.src}" alt="equipment" style="width:170px;height:130px;object-fit:cover;border:1px solid #E7E5E4;border-radius:6px;margin:0 8px 8px 0;" />`)
    .join("");

  const liftgate = [];
  if (schedule?.liftgatePickup) liftgate.push("Pickup");
  if (schedule?.liftgateDelivery) liftgate.push("Delivery");

  return `<!doctype html><html><head><meta charset="utf-8"/>
<title>K&G Request for Shipping Quote</title>
<style>
  * { box-sizing:border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color:#1C1917; margin:0; padding:40px; background:#fff; }
  .brandbar { background:#1C1917; color:#fff; padding:22px 26px; border-radius:10px; display:flex; justify-content:space-between; align-items:center; }
  .brandbar h1 { margin:0; font-size:22px; letter-spacing:-0.5px; }
  .brandbar .amp { color:#F97316; }
  .brandbar .meta { text-align:right; font-size:12px; color:#d6d3d1; line-height:1.5; }
  h2 { font-size:14px; text-transform:uppercase; letter-spacing:1px; color:#F97316; border-bottom:2px solid #F97316; padding-bottom:6px; margin:28px 0 12px; }
  table { width:100%; border-collapse:collapse; font-size:12px; }
  th { background:#1C1917; color:#fff; text-align:left; padding:8px; font-size:11px; text-transform:uppercase; }
  td { border:1px solid #E7E5E4; padding:8px; }
  ul { margin:6px 0; padding-left:20px; font-size:13px; line-height:1.7; }
  .checklist li { list-style:none; position:relative; padding-left:24px; }
  .checklist li:before { content:"☐"; position:absolute; left:0; color:#F97316; font-size:16px; }
  .closing { margin-top:26px; padding:16px; background:#FFF7ED; border:1px solid #F97316; border-radius:8px; font-size:14px; }
  .closing b { color:#EA580C; }
  .muted { color:#57534E; font-size:12px; }
  @media print { body { padding:14px; } .brandbar { -webkit-print-color-adjust:exact; print-color-adjust:exact; } th{ -webkit-print-color-adjust:exact; print-color-adjust:exact; } }
</style></head>
<body>
  <div class="brandbar">
    <div>
      <img src="${window.location.origin}/kg-logo.png" alt="K&amp;G Mowers + Tractors" style="height:54px;display:block;" />
      <div style="font-size:12px;color:#F97316;margin-top:8px;font-weight:bold;">Request for Shipping Quote</div>
    </div>
    <div class="meta">
      <div><b style="color:#fff;">${esc(COMPANY.name)}</b></div>
      <div>${esc(COMPANY.address)}</div>
      <div>${esc(COMPANY.phone)}</div>
      <div>${esc(date)}</div>
    </div>
  </div>

  <h2>Route &amp; Schedule</h2>
  <ul>
    <li><b>Pickup:</b> ${esc(pickup)}</li>
    ${stopRows || "<li>No delivery stops entered</li>"}
    <li><b>Estimated distance (round trip):</b> ${esc(effectiveMiles || 0)} miles</li>
    <li><b>Requested pickup date:</b> ${esc(schedule?.pickupDate || "TBD")}</li>
    <li><b>Requested delivery date:</b> ${esc(schedule?.deliveryDate || "TBD")}</li>
    <li><b>Liftgate required:</b> ${liftgate.length ? esc(liftgate.join(" & ")) : "None"}</li>
  </ul>

  <h2>Equipment</h2>
  <table>
    <thead><tr><th>#</th><th>Type</th><th>Brand</th><th>Model</th><th>Qty</th><th>Dimensions (L×W×H)</th><th>Weight</th></tr></thead>
    <tbody>${unitRows || `<tr><td colspan="7" class="muted">No units entered</td></tr>`}</tbody>
  </table>

  ${photoImgs ? `<h2>Equipment Photos</h2><div>${photoImgs}</div>` : ""}

  ${aiNotes ? `<h2>Shipment Notes</h2><div style="font-size:13px;line-height:1.7;white-space:pre-wrap;">${esc(aiNotes)}</div>` : ""}

  <h2>Please Include in Your Quote</h2>
  <ul class="checklist">
    <li>All-in rate for the full route</li>
    <li>Earliest available pickup date and transit time</li>
    <li>Trailer / equipment type to be used</li>
    <li>Certificate of insurance with cargo coverage amount</li>
    <li>Loading / unloading assistance or forklift / ramp needs</li>
    <li>Liftgate capability confirmation when required</li>
  </ul>

  <div class="closing">
    Please send your quote to <b>${esc(COMPANY.rfpEmail)}</b>.${
      salesAgent?.name || salesAgent?.email
        ? `<br/><span style="font-size:12px;">K&amp;G sales agent: <b>${esc(salesAgent.name || "")}</b>${
            salesAgent.email ? ` (${esc(salesAgent.email)})` : ""
          }</span>`
        : ""
    }
  </div>
</body></html>`;
}

export function printRfp(html) {
  // Open the RFP via a same-origin Blob URL (avoids document.write XSS sink).
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) {
    URL.revokeObjectURL(url);
    return false;
  }
  setTimeout(() => {
    try {
      w.focus();
      w.print();
    } catch (err) {
      devWarn("Print trigger failed", err);
    }
  }, 600);
  // Revoke once the popup is done with the document (fallback after 5 min).
  const revoke = () => URL.revokeObjectURL(url);
  try {
    w.addEventListener("afterprint", revoke);
    w.addEventListener("unload", revoke);
  } catch {
    setTimeout(revoke, 300000);
  }
  return true;
}

export { fmtMoney };
